import { useCallback, useRef, useState } from "react"
import { describeError } from "../api/http"

export interface Action {
	/** Идёт запись — кнопки экрана гасятся на это время. */
	busy: boolean
	error: string | null
	notice: string | null
	setError: (text: string | null) => void
	setNotice: (text: string | null) => void
	/** Выполняет запись. `true` — прошла, `false` — ошибка уже показана. */
	run: (call: () => Promise<unknown>, done?: string) => Promise<boolean>
	/** Погасить сообщения — например при смене группы или сайта. */
	reset: () => void
}

/**
 * Одна запись в ERP: индикатор занятости, одно сообщение об ошибке, одно
 * сообщение об успехе.
 *
 * Раньше эта тройка состояний с одинаковым `try/catch/finally` была написана
 * заново в каждой панели, где есть кнопка, — шесть раз слово в слово. Отличались
 * они лишь тем, что делать после успеха (сбросить кэш) и как переводить ошибку
 * в текст; и то и другое здесь параметры.
 */
export function useAction(
	options: {
		/** Что сделать после успешной записи — обычно сбросить кэш запросов. */
		after?: () => Promise<unknown> | void
		/** Как объяснить ошибку. По умолчанию — общее правило `describeError`. */
		describe?: (error: unknown) => string
	} = {}
): Action {
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [notice, setNotice] = useState<string | null>(null)

	// объект настроек создаётся заново на каждый рендер — держим в ref,
	// иначе `run` менялся бы следом и тянул за собой эффекты вызывающих
	const settings = useRef(options)
	settings.current = options

	const run = useCallback(async (call: () => Promise<unknown>, done?: string) => {
		setBusy(true)
		setError(null)
		setNotice(null)
		try {
			await call()
			await settings.current.after?.()
			if (done) setNotice(done)
			return true
		} catch (thrown) {
			setError((settings.current.describe ?? describeError)(thrown))
			return false
		} finally {
			setBusy(false)
		}
	}, [])

	const reset = useCallback(() => {
		setError(null)
		setNotice(null)
	}, [])

	return { busy, error, notice, setError, setNotice, run, reset }
}
